import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Avatar, Stack, TextField, MenuItem, Snackbar, Alert,
    Skeleton, CircularProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { profileUpdated } from '../redux/slices/authSlice';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../theme';
import http, { apiErrorMessage } from '../Api/http';
import { GetMyProfile, UpdateMyProfile, GetEmployees, GetEmployeeById, UpdateEmployee } from '../Api/Api';
import { joinName } from '../utils/format';
import { toEditForm, buildUpdatePayload, GENDERS, MARITAL, BLOOD } from './EmployeeDetailPage';

const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '11px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const field = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 14, bgcolor: '#F8FAFC' } };
const isValidPhone = (raw = '') => {
    const digits = String(raw).replace(/\D/g, '');
    return !raw || (digits.length >= 10 && digits.length <= 15);
};
const todayInput = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Personal details a person normally keeps up to date themselves.
// The server lets a person change only these (name, codes, employment, ids and bank stay with HR).
const SELF_EDITABLE = [
    'gender', 'dateOfBirth', 'maritalStatus', 'bloodGroup', 'nationality', 'personalEmail', 'personalMobile', 'alternateMobile',
    'presentAddress', 'permanentAddress', 'educations', 'certifications', 'previousEmployments',
    'emergencyContactName', 'emergencyRelationship', 'emergencyMobile', 'emergencyAlternateNumber', 'emergencyAddress',
];
const PERSONAL = [
    { k: 'firstName', label: 'First Name', locked: true },
    { k: 'lastName', label: 'Last Name', locked: true },
    { k: 'gender', label: 'Gender', select: GENDERS },
    { k: 'dateOfBirth', label: 'Date of Birth', date: true },
    { k: 'maritalStatus', label: 'Marital Status', select: MARITAL },
    { k: 'bloodGroup', label: 'Blood Group', select: BLOOD },
    { k: 'nationality', label: 'Nationality' },
    { k: 'personalEmail', label: 'Personal Email', email: true, wide: true },
    { k: 'personalMobile', label: 'Phone', phone: true },
    { k: 'alternateMobile', label: 'Alternate Mobile', phone: true },
];
const ADDRESS = [
    { k: 'line1', label: 'Address Line 1', full: true }, { k: 'line2', label: 'Address Line 2', full: true },
    { k: 'city', label: 'City' }, { k: 'state', label: 'State' },
    { k: 'country', label: 'Country' }, { k: 'postalCode', label: 'Postal Code' },
];

const GRID = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5, rowGap: 2.6 };
const SPAN = { gridColumn: '1 / -1' };
const chip = (text) => (
    <Box key={text} component="span" sx={{ px: 1.1, py: 0.3, borderRadius: '20px', bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, color: PRIMARY, fontSize: 11.5, fontWeight: 700 }}>{text}</Box>
);

// "My Profile": your own employee record, with the common personal details open for editing.
export default function MyProfilePage({ embedded = false, section }) {
    const dispatch = useDispatch();
    const auth = useSelector((s) => s.auth);
    const { setCrumb } = useOutletContext() || {};
    useEffect(() => { if (setCrumb && !embedded) setCrumb('My Profile'); }, [setCrumb, embedded]);

    const [emp, setEmp] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [tried, setTried] = useState(false);
    const [snack, setSnack] = useState(null);

    // When the server has no My Profile calls (or they fail), the record is found through the employee list instead,
    // and saved with the HR-level update, which an admin is allowed to use. `legacy` remembers which route is in use.
    const legacy = useRef(false);
    const findOwnId = useCallback(async () => {
        const u = auth.user || {};
        const keys = [u.employeeCode, u.loginId, auth.email, u.email].filter(Boolean);
        for (const k of keys) {
            // eslint-disable-next-line no-await-in-loop
            const { data: body } = await http.get(GetEmployees, { params: { search: k, page: 1, pageSize: 50 } }).catch(() => ({ data: null }));
            const items = body?.data?.items || [];
            const key = String(k).toLowerCase();
            const hit = items.find((e) => [e.employeeCode, e.personalEmail, e.companyEmployeeId].some((v) => v && String(v).toLowerCase() === key));
            if (hit) return hit.id;
        }
        return null;
    }, [auth.user, auth.email]);

    const load = useCallback(async () => {
        setLoading(true); setLoadError('');
        try {
            let data = null;
            if (!legacy.current) {
                try {
                    const { data: body } = await http.get(GetMyProfile);
                    if (body?.error) throw new Error(body.message || 'Could not load your profile.');
                    data = body?.data || null;
                } catch (err) {
                    const st = err?.response?.status;
                    // Not available on this server: fall back. Anything else is a real error to show.
                    if (st === 404 || st === 405 || !err?.response) legacy.current = true; else throw err;
                }
            }
            if (legacy.current) {
                const id = await findOwnId();
                if (id == null) throw new Error('We could not find an employee record for your account.');
                const { data: body } = await http.get(GetEmployeeById, { params: { id } });
                if (body?.error) throw new Error(body.message || 'Could not load your profile.');
                data = body?.data || null;
            }
            setEmp(data);
            setForm(data ? toEditForm(data) : null);
        } catch (err) {
            setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load your profile.') : err.message);
        } finally {
            setLoading(false);
        }
    }, [findOwnId]);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setAddr = (which, k) => (e) => setForm((f) => ({ ...f, [which]: { ...f[which], [k]: e.target.value } }));

    const today = todayInput();
    const dobFuture = Boolean(form?.dateOfBirth) && form.dateOfBirth > today;
    const invalid = (f) => (
        (f.phone && !isValidPhone(form?.[f.k]))
        || (f.email && Boolean(form?.[f.k]) && !/\S+@\S+\.\S+/.test(form[f.k]))
        || (f.k === 'dateOfBirth' && dobFuture)
    );
    const hasErrors = form ? PERSONAL.some(invalid) : true;

    const save = async () => {
        setTried(true);
        if (hasErrors) return;
        setSaving(true);
        try {
            // Only the self-editable fields are sent; the lists (education, earlier jobs) go back as they were loaded.
            const full = buildUpdatePayload(emp.id, form);
            const payload = Object.fromEntries(SELF_EDITABLE.map((k) => [k, full[k]]));
            // The fallback route needs the whole record (the HR update replaces everything), not just the safe fields.
            const { data: body } = legacy.current
                ? await http.put(UpdateEmployee, full)
                : await http.put(UpdateMyProfile, payload);
            if (body?.error) throw new Error(body.message || 'Could not save your profile.');
            dispatch(profileUpdated({ phone: form.personalMobile }));
            setSnack({ sev: 'success', msg: 'Profile updated.' });
            setTried(false);
            await load();
        } catch (err) {
            setSnack({ sev: 'error', msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not save your profile.') : err.message });
        } finally {
            setSaving(false);
        }
    };

    const fullName = joinName(emp?.firstName, emp?.lastName) || auth.userName || 'My Profile';
    const showPersonal = !section || section === 'personal';
    const showAddress = !section || section === 'address';

    const personalField = (f) => {
        const bad = tried && invalid(f);
        const helper = f.k === 'dateOfBirth' && dobFuture ? 'Date of birth cannot be in the future'
            : bad && f.phone ? 'Enter a valid 10–15 digit number'
                : bad && f.email ? 'Enter a valid email'
                    : undefined;
        if (f.select) {
            return (
                <TextField key={f.k} select label={f.label} fullWidth size="small" value={form[f.k] || ''} onChange={set(f.k)} sx={field}
                    slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true, renderValue: (v) => v || <span style={{ color: '#94A3B8' }}>Select</span> } }}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {f.select.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 13.5 }}>{o}</MenuItem>)}
                </TextField>
            );
        }
        return (
            <TextField
                key={f.k} label={f.label} fullWidth size="small" type={f.date ? 'date' : 'text'} value={form[f.k] ?? ''} disabled={f.locked}
                onChange={f.phone ? (e) => setForm((p) => ({ ...p, [f.k]: e.target.value.replace(/[^\d+\s-]/g, '') })) : set(f.k)}
                error={bad || (f.k === 'dateOfBirth' && dobFuture)} helperText={helper}
                slotProps={{
                    inputLabel: { shrink: true },
                    ...(f.date ? { htmlInput: { max: today, onClick: (e) => { try { e.currentTarget.showPicker(); } catch { /* needs a user gesture */ } }, style: { cursor: 'pointer' } } } : {}),
                    ...(f.locked ? { input: { endAdornment: <LockOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /> } } : {}),
                }}
                sx={f.wide ? { ...field, gridColumn: { xs: 'auto', sm: 'span 2' } } : field}
            />
        );
    };

    const addressCard = (which, title, extra) => {
        const src = which === 'permanentAddress' && form?.sameAsPresent ? form.presentAddress : form?.[which];
        const muted = which === 'permanentAddress' && form?.sameAsPresent;
        return (
            <Box sx={{ border: '1px solid #E6EAF1', borderRadius: '12px', p: 2, bgcolor: '#fff' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <HomeRoundedIcon sx={{ fontSize: 17, color: PRIMARY }} />
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{title}</Typography>
                    </Stack>
                    {extra}
                </Stack>
                <Box sx={{ ...GRID, gridTemplateColumns: '1fr 1fr', rowGap: 2.2, columnGap: 1.5, opacity: muted ? 0.5 : 1, pointerEvents: muted ? 'none' : 'auto' }}>
                    {ADDRESS.map((f) => (
                        <TextField key={f.k} label={f.label} size="small" fullWidth value={src?.[f.k] ?? ''} onChange={setAddr(which, f.k)} sx={{ ...field, ...(f.full ? SPAN : {}) }}
                            slotProps={{ inputLabel: { shrink: true } }} />
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={embedded ? { pb: 1 } : { px: { xs: 1.5, md: 3 }, pt: 2, pb: 4, maxWidth: 1100 }}>
            {/* Who you are, with Save. */}
            <Stack direction="row" spacing={2.2} sx={{ alignItems: 'center', mb: 2.5, p: 2.2, borderRadius: '14px', background: 'linear-gradient(135deg, #F5F3FF 0%, #fff 70%)', border: '1px solid #E4DEFB', flexWrap: 'wrap', gap: 1.5 }}>
                <Avatar sx={{ width: 62, height: 62, bgcolor: '#fff', color: PRIMARY, border: `2px solid ${PRIMARY_BORDER}`, fontWeight: 800, fontSize: 22 }}>
                    {fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || <PersonRoundedIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 21, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }} noWrap>{fullName}</Typography>
                    <Stack direction="row" spacing={0.8} sx={{ mt: 0.7, flexWrap: 'wrap', gap: 0.6 }}>
                        {[emp?.employeeCode || auth.user?.loginId, emp?.designation, emp?.department].filter(Boolean).map(chip)}
                    </Stack>
                </Box>
                <Button
                    onClick={save} disabled={saving || loading || !form}
                    startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <SaveRoundedIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, height: 42, px: 2.6, borderRadius: '9px', color: '#fff', bgcolor: PRIMARY, boxShadow: `0 4px 12px -4px ${PRIMARY}99`, '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: PRIMARY, color: '#fff', opacity: 0.6 } }}
                >
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </Stack>

            {loading ? (
                <Stack spacing={2}><Skeleton variant="rounded" height={60} /><Skeleton variant="rounded" height={220} /></Stack>
            ) : loadError || !form ? (
                <Box sx={{ ...card, p: 4, textAlign: 'center' }}>
                    <ManageAccountsRoundedIcon sx={{ fontSize: 38, color: '#CBD2DD' }} />
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: '#64748B', mt: 1 }}>No profile to show</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#98A0AE', mt: 0.4 }}>{loadError || 'Your profile could not be loaded.'}</Typography>
                </Box>
            ) : (
                <Stack spacing={2.2}>
                    {showPersonal && (
                        <Box>
                            {/* Three to a row, so each box is the size it was before. */}
                            <Box sx={{ ...GRID, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>{PERSONAL.map(personalField)}</Box>
                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mt: 1.8 }}>
                                <LockOutlinedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>Your name is managed by HR. Ask them if it needs changing.</Typography>
                            </Stack>
                        </Box>
                    )}
                    {showAddress && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            {addressCard('presentAddress', 'Present address')}
                            {addressCard('permanentAddress', 'Permanent address', (
                                <FormControlLabel
                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12, fontWeight: 600, color: '#475569' } }}
                                    control={<Checkbox size="small" checked={form.sameAsPresent} onChange={() => setForm((f) => ({ ...f, sameAsPresent: !f.sameAsPresent }))} sx={{ p: 0.5, color: PRIMARY_BORDER, '&.Mui-checked': { color: PRIMARY } }} />}
                                    label="Same as present"
                                />
                            ))}
                        </Box>
                    )}
                </Stack>
            )}

            <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ borderRadius: '9px' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
